import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import CheckoutFinalizing from "./CheckoutFinalizing";

export default function ProtectedRoute() {
  const { isAuthenticated, isOnboarded, loading } = useAuth();
  const location = useLocation();

  // Once the merchant is onboarded the pending-checkout flag has done its job.
  // It has to be cleared here: CheckoutFinalizing's own cleanup never runs,
  // because this component swaps it for <Outlet/> on the same render that
  // isOnboarded flips. Left behind, the flag shows "finalizing" to the next
  // un-subscribed login in this tab. (Hook stays above every early return.)
  useEffect(() => {
    if (isOnboarded) sessionStorage.removeItem("prep_checkout_pending");
  }, [isOnboarded]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Paywall: an authenticated merchant who hasn't completed Stripe checkout
  // must NOT reach the product. Two exceptions to bouncing them to onboarding:
  // they just returned from Stripe (?checkout=success) or we stamped a pending
  // flag before redirecting to Stripe — in that window we poll for the webhook
  // instead of bouncing a merchant who already paid.
  if (!isOnboarded) {
    const justCheckedOut =
      new URLSearchParams(location.search).get("checkout") === "success" ||
      sessionStorage.getItem("prep_checkout_pending") === "1";
    if (justCheckedOut) {
      return <CheckoutFinalizing />;
    }
    return <Navigate to="/onboarding/step/details" replace />;
  }

  return <Outlet />;
}
